import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import type { Member } from '@nexbit/types'

const TOKEN_KEY = 'nexbit_admin_token'

/** A partner is either a top-tier `master` (owns sub-agents) or a leaf `agent`. */
export type PartnerLevel = 'master' | 'agent'

interface AuthState {
  user: Member | null
  token: string | null
  /** Partner tier for role==='partner'; null for admins/members. */
  level: PartnerLevel | null
  /** Admin per-menu permissions; null = super admin (all menus). Non-admins: null. */
  permissions: string[] | null
  loading: boolean
}

export interface LoginResult {
  user: Member
  level: PartnerLevel | null
}

interface AuthContextValue extends AuthState {
  login: (token: string) => Promise<LoginResult>
  logout: () => void
  isAdmin: boolean
  isAgent: boolean
  isMaster: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

async function fetchMe(token: string): Promise<{ member: Member; permissions: string[] | null }> {
  const res = await fetch('/api/auth/me', {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) throw new Error('Unauthorized')
  const data = (await res.json()) as { member: Member; role: string; permissions: string[] | null }
  return { member: data.member, permissions: data.permissions ?? null }
}

/**
 * Resolve a partner's tier from `/agent/profile`. Only meaningful for
 * role==='partner'; returns null otherwise. Fails closed to 'agent' (no master
 * privileges) if the profile call errors, so a transient failure can never
 * escalate an agent into the master surface.
 */
async function fetchLevel(token: string, role: string): Promise<PartnerLevel | null> {
  if (role !== 'partner') return null
  try {
    const res = await fetch('/api/agent/profile', {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) return 'agent'
    const data = (await res.json()) as { level?: PartnerLevel }
    return data.level === 'master' ? 'master' : 'agent'
  } catch {
    return 'agent'
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: sessionStorage.getItem(TOKEN_KEY),
    level: null,
    permissions: null,
    loading: true,
  })

  useEffect(() => {
    const stored = sessionStorage.getItem(TOKEN_KEY)
    if (stored) {
      fetchMe(stored)
        .then(async ({ member, permissions }) => {
          const level = await fetchLevel(stored, member.role)
          setState({ user: member, token: stored, level, permissions, loading: false })
        })
        .catch(() => {
          sessionStorage.removeItem(TOKEN_KEY)
          setState({ user: null, token: null, level: null, permissions: null, loading: false })
        })
    } else {
      setState((s) => ({ ...s, loading: false }))
    }
  }, [])

  const login = useCallback(async (token: string): Promise<LoginResult> => {
    sessionStorage.setItem(TOKEN_KEY, token)
    const { member, permissions } = await fetchMe(token)
    const level = await fetchLevel(token, member.role)
    setState({ user: member, token, level, permissions, loading: false })
    return { user: member, level }
  }, [])

  const logout = useCallback(() => {
    const token = sessionStorage.getItem(TOKEN_KEY)
    sessionStorage.removeItem(TOKEN_KEY)
    setState({ user: null, token: null, level: null, permissions: null, loading: false })
    if (token) {
      fetch('/api/auth/logout', { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
        .catch(() => null)
    }
  }, [])

  const isAgent = state.user?.role === 'partner'

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        isAdmin: state.user?.role === 'admin',
        isAgent,
        isMaster: isAgent && state.level === 'master',
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
